<script setup lang="ts">
import { computed, ref, useId, useSlots } from 'vue'

type SelectOption = {
  value: string | number
  label: string
  disabled?: boolean
}

const props = withDefaults(
  defineProps<{
    search: string
    itemsPerPage?: string
    itemsPerPageOptions?: SelectOption[]
    searchPlaceholder?: string
    filtersCount?: number
  }>(),
  {
    itemsPerPage: '50',
    itemsPerPageOptions: () => [
      { value: '10', label: '10' },
      { value: '25', label: '25' },
      { value: '50', label: '50' },
      { value: '100', label: '100' },
    ],
    searchPlaceholder: 'Sök',
    filtersCount: 0,
  },
)

const emit = defineEmits<{
  'update:search': [value: string]
  'update:itemsPerPage': [value: string]
  'reset-filters': []
}>()

const slots = useSlots()
const uid = useId()

const searchModel = computed({
  get: () => props.search,
  set: (value: string) => emit('update:search', value),
})

const itemsPerPageModel = computed({
  get: () => props.itemsPerPage || '',
  set: (value: string) => emit('update:itemsPerPage', value),
})

const filtersId = computed(() => `list-header-filters-${uid}`)
const filtersOpen = ref(false)
const hasFilters = computed(() => Boolean(slots.filters))
const filtersCountSafe = computed(() => Math.max(0, Number(props.filtersCount) || 0))
const hasActiveFilters = computed(() => filtersCountSafe.value > 0)

function toggleFilters() {
  if (!hasFilters.value) return
  filtersOpen.value = !filtersOpen.value
}

function closeFilters() {
  filtersOpen.value = false
}
</script>

<template>
  <section class="lp-card app-list-header">
    <div class="app-list-header__row">
      <AppTextField
        v-model="searchModel"
        class="app-list-header__search"
        density="compact"
        icon="hero-magnifying-glass"
        :placeholder="searchPlaceholder"
        aria-label="Sök"
        autocomplete="off"
      />

      <div class="app-list-header__spacer" aria-hidden="true" />

      <div class="app-list-header__actions">
        <AppSelect
          v-if="itemsPerPage"
          v-model="itemsPerPageModel"
          class="app-list-header__per-page"
          density="compact"
          aria-label="Rader per sida"
          :options="itemsPerPageOptions"
        />

        <AppButton
          v-if="hasFilters"
          class="app-btn--tonal-primary app-list-header__btn"
          :class="{ 'app-list-header__btn--active': hasActiveFilters }"
          variant="secondary"
          size="sm"
          type="button"
          :aria-expanded="filtersOpen ? 'true' : 'false'"
          :aria-controls="filtersId"
          aria-label="Filter"
          @click="toggleFilters"
        >
          <AppIcon name="hero-adjustments-horizontal" size="md" />
          Filter
          <span
            v-if="hasActiveFilters"
            class="app-badge app-badge--primary app-list-header__filters-badge"
            aria-hidden="true"
          >
            {{ filtersCountSafe }}
          </span>
          <AppIcon
            class="app-list-header__filters-chevron"
            :class="{ 'app-list-header__filters-chevron--open': filtersOpen }"
            name="hero-chevron-down"
            size="md"
          />
        </AppButton>

        <AppButton
          v-if="hasFilters && hasActiveFilters"
          class="app-btn--tonal-secondary app-list-header__btn"
          variant="secondary"
          size="sm"
          type="button"
          aria-label="Nollställ filter"
          @click="emit('reset-filters')"
        >
          <AppIcon name="hero-arrow-path" size="md" />
          Nollställ
        </AppButton>

        <slot name="actions-right" />
      </div>
    </div>

    <div v-if="hasFilters" class="app-list-header__filters">
      <Transition name="app-collapse">
        <div
          v-show="filtersOpen"
          :id="filtersId"
          class="app-list-header__filters-panel"
          role="region"
          aria-label="Filter"
        >
          <div class="app-filters-panel">
            <slot name="filters" :close="closeFilters" />
          </div>
        </div>
      </Transition>
    </div>
  </section>
</template>
